import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, Check, ArrowLeft, Upload, Plus, AlertTriangle, ArrowDown, WifiOff, Camera, X } from 'lucide-react';
import { toast } from 'sonner';
import { CountEntryRow } from './CountEntryRow';
import ProductSearchModal from './ProductSearchModal';
import {
  getCountPackage,
  saveLocalCount,
  saveAllLocalCounts,
  downloadCountPackage,
  COUNT_SYNC_STATES,
} from '../../services/countOfflineService';
import api from '../../services/api';
import { subcategoryComparator } from '../../lib/subcategoryOrder';

export default function CountSessionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sessionId = parseInt(id, 10);

  const [pkg, setPkg] = useState(null);
  const [localCounts, setLocalCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | uncounted | counted
  const listContainerRef = useRef(null);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [unmatchedItems, setUnmatchedItems] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const flushRegistryRef = useRef(new Set());
  const localCountsRef = useRef({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);

  // Track online/offline
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);



  // Load package from IndexedDB — defensive: never let an error leave the page stuck
  useEffect(() => {
    async function load() {
      try {
        const stored = await getCountPackage(sessionId);
        if (!stored) {
          if (!navigator.onLine) {
            // Offline + no local package: show clear error, do NOT redirect
            setLoadError('offline_no_package');
            setLoading(false);
            return;
          }
          toast.error('Count package not found. Please download from the session picker.');
          navigate('/count/sessions');
          return;
        }
        setPkg(stored.package);
        setLocalCounts(stored.local_counts || {});
        setLoading(false);
      } catch (err) {
        console.error('Failed to load count package from IndexedDB:', err);
        setLoadError('load_failed');
        setLoading(false);
      }
    }
    load();
  }, [sessionId, navigate]);

  // Load unmatched items from server (best-effort, won't block offline)
  useEffect(() => {
    if (!sessionId) return;
    api.get(`/count/sessions/${sessionId}/unmatched-items`)
      .then(res => setUnmatchedItems(res.data.items || []))
      .catch(() => {});
  }, [sessionId]);

  // Keep ref in sync with state
  useEffect(() => { localCountsRef.current = localCounts; }, [localCounts]);

  // WAL recovery: on mount, check for crash-recovery data in localStorage
  useEffect(() => {
    const walKey = `count_wal_${sessionId}`;
    try {
      const wal = localStorage.getItem(walKey);
      if (wal) {
        const walCounts = JSON.parse(wal);
        localStorage.removeItem(walKey);
        // Merge WAL into IndexedDB and state
        saveAllLocalCounts(sessionId, walCounts)
          .then(() => setLocalCounts(prev => ({ ...prev, ...walCounts })))
          .catch(() => {});
      }
    } catch (e) { /* ignore corrupt WAL */ }
  }, [sessionId]);

  // Flush all children + WAL on beforeunload / visibilitychange
  useEffect(() => {
    const flushAll = () => {
      flushRegistryRef.current.forEach(fn => fn());
    };
    const handleBeforeUnload = () => {
      flushAll();
      // Write WAL to localStorage (synchronous) as belt-and-suspenders backup
      try {
        localStorage.setItem(
          `count_wal_${sessionId}`,
          JSON.stringify(localCountsRef.current)
        );
      } catch (e) { /* quota exceeded — acceptable */ }
    };
    const handleVisibilityChange = () => {
      if (document.hidden) flushAll();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [sessionId]);

  // Immediate save to IndexedDB — children debounce internally
  const handleCountChange = useCallback((lineId, countData) => {
    setLocalCounts(prev => ({ ...prev, [lineId]: countData }));
    saveLocalCount(sessionId, lineId, countData).catch(e =>
      console.error('Failed to save count:', e)
    );
  }, [sessionId]);

  // Periodic auto-sync: push local counts to server every 30s if online and dirty
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!navigator.onLine) return;
      const stored = await getCountPackage(sessionId).catch(() => null);
      if (!stored || stored.sync_state !== COUNT_SYNC_STATES.COUNTING) return;
      const counts = stored.local_counts || {};
      const entries = Object.entries(counts);
      if (entries.length === 0) return;
      try {
        const payload = entries
          // Belt-and-suspenders: exclude null/empty/NaN quantities so that
          // adding an empty split on an uncounted row never pushes a ghost
          // entry to the server and corrupts a previously-calculated snapshot.
          .filter(([, c]) => {
            if (c.quantity === undefined || c.quantity === null || c.quantity === '') return false;
            const parsed = parseFloat(c.quantity);
            return !Number.isNaN(parsed);
          })
          .map(([lineId, c]) => ({
            line_id: parseInt(lineId, 10),
            quantity: parseFloat(c.quantity),
            unit_id: c.unit_id || null,
            unit_label: c.unit_label || null,
            notes: c.notes || '',
          }));
        if (payload.length === 0) return;
        await api.put(`/stocktake/sessions/${sessionId}/entries`, { entries: payload });
        // Update sync state
        await saveAllLocalCounts(sessionId, counts);
      } catch (_) { /* retry next cycle */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [sessionId]);

  // Handle product assigned from search modal — re-download package to get new line
  // Preserves all existing local counts (downloadCountPackage merges automatically)
  const handleProductAssigned = useCallback(async (result) => {
    toast.success(`Added "${result.product.product_name}" to count sheet`);
    // Re-download the package to include the newly added line
    // downloadCountPackage now preserves local counts during re-download
    const refreshed = await downloadCountPackage(sessionId);
    if (refreshed.success) {
      setPkg(refreshed.package);
      const stored = await getCountPackage(sessionId);
      if (stored) setLocalCounts(stored.local_counts || {});
    }
  }, [sessionId]);

  // Handle unmatched item created
  const handleUnmatchedCreated = useCallback((item) => {
    setUnmatchedItems(prev => [item, ...prev]);
    toast.success(`Logged "${item.product_name}" as unmatched — flagged for review`);
  }, []);

  const session = pkg?.session;
  const lines = useMemo(() => pkg?.count_sheet_lines || [], [pkg?.count_sheet_lines]);
  const products = pkg?.products || {};

  // Available categories and subcategories for filter dropdowns
  const availableCategories = useMemo(() => {
    const cats = new Set();
    lines.forEach(line => {
      const p = products[String(line.beverage_product_id)];
      const cat = p?.category || line.category_snapshot;
      if (cat) cats.add(cat);
    });
    return [...cats].sort();
  }, [lines, products]);

  const availableSubcategories = useMemo(() => {
    const subs = new Set();
    lines.forEach(line => {
      const p = products[String(line.beverage_product_id)];
      const sub = p?.subcategory;
      if (sub) subs.add(sub);
    });
    return [...subs].sort();
  }, [lines, products]);

  const activeFilterCount = selectedCategories.length + selectedSubcategories.length;

  // Memoized filter + search + alphabetical sort
  const filteredLines = useMemo(() => {
    const filtered = lines.filter(line => {
      const isCounted = localCounts[line.id]?.quantity != null;
      if (filter === 'counted' && !isCounted) return false;
      if (filter === 'uncounted' && isCounted) return false;

      // Category filter
      if (selectedCategories.length > 0) {
        const p = products[String(line.beverage_product_id)];
        const cat = p?.category || line.category_snapshot;
        if (!cat || !selectedCategories.includes(cat)) return false;
      }

      // Subcategory filter
      if (selectedSubcategories.length > 0) {
        const p = products[String(line.beverage_product_id)];
        const sub = p?.subcategory;
        if (!sub || !selectedSubcategories.includes(sub)) return false;
      }

      // Search by product name, product code, and location
      if (search) {
        const q = search.toLowerCase();
        const name = (line.product_name_snapshot || '').toLowerCase();
        const loc = (line.location_name_snapshot || '').toLowerCase();
        const p = products[String(line.beverage_product_id)];
        const code = (p?.product_code || '').toLowerCase();
        return name.includes(q) || loc.includes(q) || code.includes(q);
      }
      return true;
    });

    // Sort alphabetically by product name
    filtered.sort((a, b) => {
      const nameA = (a.product_name_snapshot || '').toLowerCase();
      const nameB = (b.product_name_snapshot || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return filtered;
  }, [lines, localCounts, filter, search, selectedCategories, selectedSubcategories, products]);

  // iter340 v4 — group filtered lines by subcategory with a heading row,
  // products sorted alphabetically within each group. Lines with no
  // subcategory fall under "Other" so nothing is dropped.
  const groupedFilteredLines = useMemo(() => {
    const groups = new Map();
    for (const line of filteredLines) {
      const p = products[String(line.beverage_product_id)];
      const sub = (p?.subcategory && String(p.subcategory).trim()) || 'Other';
      if (!groups.has(sub)) groups.set(sub, []);
      groups.get(sub).push(line);
    }
    // Sort group keys by the canonical subcategory order; "Other" pinned to the end.
    const keys = [...groups.keys()].sort(subcategoryComparator);
    return keys.map(k => ({ subcategory: k, lines: groups.get(k) }));
  }, [filteredLines, products]);

  const totalLines = lines.length;
  const countedLines = useMemo(
    () => lines.filter(l => localCounts[l.id]?.quantity != null).length,
    [lines, localCounts]
  );
  const progress = totalLines > 0 ? Math.round((countedLines / totalLines) * 100) : 0;

  // iter374 — Jump to next uncounted product (visually first, not API-
  // order first). Walks the same grouped order the screen renders, so
  // the user lands on the immediately-visible next row instead of one
  // hidden behind a different subcategory header.
  const jumpToNextUncounted = useCallback(() => {
    let nextLine = null;
    outer: for (const g of groupedFilteredLines) {
      for (const ln of g.lines) {
        if (localCounts[ln.id]?.quantity == null) { nextLine = ln; break outer; }
      }
    }
    if (nextLine) {
      const el = document.querySelector(`[data-testid="count-entry-row-${nextLine.id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = el.querySelector('input[type="number"]');
        if (input) setTimeout(() => input.focus(), 350);
      }
    } else {
      toast.info('All products counted!');
    }
  }, [groupedFilteredLines, localCounts]);

  // Handle filter change — scroll to top
  const handleFilterChange = useCallback((f) => {
    setFilter(f);
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0;
    }
  }, []);

  // iter374 — Auto-advance: use the GROUPED rendered order, not the raw
  // `lines` array. Previously the for-loop walked `lines` which was in
  // API/DB order; the screen renders by subcategory groups (Beers,
  // Wines, …). The first uncounted next-in-array row was often in a
  // different subcategory group, so the user's eye saw it as "skipped 2
  // products". Now we walk the same list the user sees.
  const handleEntryAdvance = useCallback((currentLineId) => {
    const flat = [];
    for (const g of groupedFilteredLines) {
      for (const ln of g.lines) flat.push(ln);
    }
    const currentIndex = flat.findIndex(l => l.id === currentLineId);
    if (currentIndex === -1) return;
    for (let i = currentIndex + 1; i < flat.length; i++) {
      if (localCounts[flat[i].id]?.quantity == null) {
        const el = document.querySelector(`[data-testid="count-entry-row-${flat[i].id}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = el.querySelector('input[type="number"]');
          if (input) setTimeout(() => input.focus(), 200);
        }
        return;
      }
    }
    // All remaining are counted
    toast.info('All remaining products counted!');
  }, [groupedFilteredLines, localCounts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4 text-center" data-testid="count-load-error">
        {loadError === 'offline_no_package' ? (
          <>
            <WifiOff className="w-12 h-12 text-slate-300 mb-4" />
            <h2 className="text-lg font-semibold text-slate-700 mb-2">Offline — No Local Package</h2>
            <p className="text-sm text-slate-500 max-w-xs mb-4">
              This session hasn't been downloaded for offline use. Connect to the internet and download it from the session picker.
            </p>
          </>
        ) : (
          <>
            <AlertTriangle className="w-12 h-12 text-amber-400 mb-4" />
            <h2 className="text-lg font-semibold text-slate-700 mb-2">Failed to Load Count Data</h2>
            <p className="text-sm text-slate-500 max-w-xs mb-4">
              Could not read the local count package. Try refreshing or re-downloading from the session picker.
            </p>
          </>
        )}
        <Button variant="outline" onClick={() => navigate('/count/sessions')} data-testid="count-error-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Sessions
        </Button>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="count-session-page">
      {/* Offline banner */}
      {!isOnline && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-sm text-amber-700" data-testid="count-offline-banner">
          <WifiOff className="w-4 h-4 shrink-0" />
          <span>Offline — counting from local storage</span>
        </div>
      )}
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200">
        {/* Session info bar */}
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/count/sessions')}
            className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="count-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold text-slate-900 truncate" data-testid="count-session-site">
              {session.site_name}
            </h1>
            <p className="text-xs text-slate-400">{session.session_reference} &middot; {session.session_date}</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowProductSearch(true)}
            className="shrink-0"
            data-testid="count-add-product-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/count/sessions/${sessionId}/observations`)}
            className="shrink-0"
            data-testid="count-observations-btn"
          >
            <Camera className="w-4 h-4 mr-1" />
            Notes
          </Button>
          <Button
            size="sm"
            onClick={() => navigate(`/count/sessions/${sessionId}/sync`)}
            className="shrink-0"
            data-testid="count-finish-sync-btn"
          >
            <Upload className="w-4 h-4 mr-1.5" />
            Finish
          </Button>
        </div>

        {/* Progress bar */}
        <div className="px-4 pb-2">
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span data-testid="count-progress-text">{countedLines}/{totalLines} counted</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-slate-800 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
              data-testid="count-progress-bar"
            />
          </div>
        </div>

        {/* Search + filter */}
        <div className="px-4 pb-2 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or code..."
              className="pl-9 h-10"
              data-testid="count-search-input"
            />
          </div>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden shrink-0">
            {['all', 'uncounted', 'counted'].map(f => (
              <button
                key={f}
                onClick={() => handleFilterChange(f)}
                className={`px-3 py-2.5 text-sm font-medium capitalize transition-colors min-h-[44px] touch-manipulation ${
                  filter === f
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-500 hover:bg-slate-50 active:bg-slate-100'
                }`}
                data-testid={`count-filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Category / Subcategory filters — always visible */}
        {(availableCategories.length > 0 || availableSubcategories.length > 0) && (
          <div className="px-4 pb-3 border-t border-slate-100 pt-2 space-y-2" data-testid="count-category-panel">
            {availableCategories.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Category</p>
                <div className="flex flex-wrap gap-1.5">
                  {availableCategories.map(cat => {
                    const isActive = selectedCategories.includes(cat);
                    return (
                      <button
                        key={cat}
                        onClick={() => setSelectedCategories(prev => isActive ? prev.filter(c => c !== cat) : [...prev, cat])}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors touch-manipulation ${
                          isActive ? 'bg-cyan-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 active:bg-slate-300'
                        }`}
                        data-testid={`count-cat-${cat}`}
                      >
                        {cat.replace(/_/g, ' ')}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {availableSubcategories.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Sub-category</p>
                <div className="flex flex-wrap gap-1.5">
                  {availableSubcategories.map(sub => {
                    const isActive = selectedSubcategories.includes(sub);
                    return (
                      <button
                        key={sub}
                        onClick={() => setSelectedSubcategories(prev => isActive ? prev.filter(s => s !== sub) : [...prev, sub])}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors touch-manipulation ${
                          isActive ? 'bg-cyan-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 active:bg-slate-300'
                        }`}
                        data-testid={`count-subcat-${sub}`}
                      >
                        {sub}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {activeFilterCount > 0 && (
              <button
                onClick={() => { setSelectedCategories([]); setSelectedSubcategories([]); }}
                className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                data-testid="count-clear-filters"
              >
                <X className="w-3 h-3" /> Clear filters ({activeFilterCount})
              </button>
            )}
          </div>
        )}
      </div>

      {/* Scrollable product list */}
      <div className="flex-1 overflow-y-auto" ref={listContainerRef}>
        {filteredLines.length === 0 && unmatchedItems.length === 0 ? (
          <div className="text-center py-16 text-slate-400" data-testid="count-no-results">
            <p className="text-sm">
              {search ? 'No products match your search' : filter === 'counted' ? 'No products counted yet' : 'No uncounted products'}
            </p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-slate-100">
              {groupedFilteredLines.map(group => (
                <div key={group.subcategory} data-testid={`count-subcategory-group-${group.subcategory.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div className="sticky top-0 z-[1] bg-slate-100/95 backdrop-blur px-4 py-1.5 border-y border-slate-200">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                      {group.subcategory}
                      <span className="ml-2 text-slate-400 normal-case font-normal">
                        ({group.lines.length})
                      </span>
                    </p>
                  </div>
                  {group.lines.map(line => (
                    <CountEntryRow
                      key={line.id}
                      line={line}
                      product={products[String(line.beverage_product_id)]}
                      localCount={localCounts[line.id]}
                      onCountChange={handleCountChange}
                      onAdvance={handleEntryAdvance}
                      flushRegistry={flushRegistryRef}
                    />
                  ))}
                </div>
              ))}
            </div>

            {/* Unmatched items section */}
            {unmatchedItems.length > 0 && (
              <div className="mt-2" data-testid="count-unmatched-section">
                <div className="px-4 py-2 bg-amber-50 border-y border-amber-200">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <span className="text-xs font-medium text-amber-800">
                      {unmatchedItems.length} unmatched {unmatchedItems.length === 1 ? 'item' : 'items'} — pending review
                    </span>
                  </div>
                </div>
                {unmatchedItems.map(item => (
                  <div key={item.id} className="px-4 py-3 bg-amber-50/30 border-b border-amber-100 flex items-center justify-between" data-testid={`unmatched-item-${item.id}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{item.product_name}</p>
                      <p className="text-xs text-slate-400">
                        {item.quantity != null ? `${item.quantity} ${item.unit_label || ''}` : 'No quantity'}
                        {item.notes ? ` — ${item.notes}` : ''}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                      {item.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Floating counted summary + jump to next */}
      {countedLines > 0 && countedLines < totalLines && (
        <div className="fixed bottom-16 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          <Badge className="bg-slate-900 text-white px-4 py-2 text-sm shadow-lg rounded-full" data-testid="count-floating-badge">
            <Check className="w-4 h-4 mr-1.5" />
            {countedLines} of {totalLines} counted
          </Badge>
          <button
            onClick={jumpToNextUncounted}
            className="bg-slate-900 text-white p-2.5 shadow-lg rounded-full hover:bg-slate-800 active:scale-90 transition-transform touch-manipulation"
            title="Jump to next uncounted"
            data-testid="count-jump-next-btn"
          >
            <ArrowDown className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Product Search Modal */}
      <ProductSearchModal
        isOpen={showProductSearch}
        onClose={() => setShowProductSearch(false)}
        sessionId={sessionId}
        siteId={session.site_id}
        context="count"
        onProductAssigned={handleProductAssigned}
        onUnmatchedCreated={handleUnmatchedCreated}
      />
    </div>
  );
}
